import React, { useEffect, useState } from 'react';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { StyledLabel } from '../../../../components/forms/styles';
import { type AutocompleteOption } from '../../../../components/forms/types';
import { UserRoles } from '../../../../components/roster/types';
import { USERS } from '../../../../config/apiPaths';
import { type PaginatedUsers } from '../../../../types/apiTypes';
import { getUserUuidHeaders } from '../../../../utils/httpUtils';
import { getUrls } from '../../../../utils/url';
import { ClassContainer } from '../../ClassContainer';
import { StyledInputContainer } from '../index.styles';

const StyledTextField = styled(TextField)`
    .MuiOutlinedInput-notchedOutline {
        border-color: ${(props) => props.theme.colors.border};
    }

    .MuiInputBase-root {
        padding: 0;
    }

    .MuiInputBase-input {
        padding: 0.5rem 0.75rem;
    }
`;

const AdditionalInstructors: React.FC = () => {
    const [options, setOptions] = useState<AutocompleteOption[]>([]);
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [inputTimer, setInputTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

    const { api } = getUrls();
    const { t } = useTranslation('create_edit_class');

    const { control } = useContainer(ClassContainer);

    const userUuid = window.localStorage.getItem('user_uuid');

    const formatUserLabel = (user) => {
        if (user.user_name && user.email) {
            return `${user.user_name} <${user.email}>`;
        }
        else if (user.user_name && user.ext_user_id) {
            return `${user.user_name} [${user.ext_user_id}]`;
        }

        return user.email || user.ext_user_id || '';
    };
    const extractUserName = (label: string): string => {
        if (label.includes('<')) {
            const [userName] = label.split('<');
            return userName.trim();
        }
        return label.trim();
    };

    const getInstructors = async (search, signal): Promise<AutocompleteOption[]> => {
        const config = {
            params: {
                limit: 10,
                roles: UserRoles.Teacher,
                search: search.toLocaleLowerCase()
            },
            headers: getUserUuidHeaders(userUuid as string),
            signal
        };

        const {
            data: { data }
        } = await axios.get<PaginatedUsers>(`${api}${USERS}`, config);

        const filtered = data.filter((user) => !!user.email || !!user.ext_user_id);

        return filtered.map((user) => ({
            label: formatUserLabel(user),
            value: user.email
        }));
    };

    const { data, isFetching } = useQuery({
        initialData: [],
        enabled: open,
        queryKey: [USERS, searchValue],
        queryFn: async ({ signal }) => getInstructors(searchValue, signal)
    });

    useEffect(() => {
        setOptions(data);
    }, [data]);

    const onInputChange = (event, value) => {
        if (inputTimer) clearTimeout(inputTimer);

        const timer = setTimeout(() => {
            setSearchValue(value);
        }, 300);

        setInputTimer(timer);
    };

    const mapOptionsToValues = (values: AutocompleteOption[]) =>
        values.map((value) => {
            const label = value.label;
            let userName = '';
            let email: string | null = null;
            let extUserId: string | null = null;

            if (label.includes('<')) {
                [userName, email] = label.split('<');
                if (email) {
                    email = email.replace(/>/g, '').trim();
                }
                userName = userName?.trim();
            }
            else if (label.includes('[')) {
                [userName, extUserId] = label.split('[');
                if (extUserId) {
                    extUserId = extUserId.replace(/\]/g, '').trim();
                }
                userName = userName?.trim();
            }

            return {
                email: email,
                ext_user_id: extUserId,
                user_name: userName
            };
        });

    return (
        <StyledInputContainer>
            <Controller
                name="additionalInstructors"
                control={control}
                render={({ field }) => (
                    <>
                        <StyledLabel htmlFor="additionalInstructors">
                            {t('advanced.additional-instructors')}
                        </StyledLabel>
                        <Autocomplete
                            multiple
                            size="small"
                            onChange={(e, values) => field.onChange(mapOptionsToValues(values))}
                            onOpen={() => setOpen(true)}
                            onClose={() => setOpen(false)}
                            options={options}
                            loading={isFetching}
                            onInputChange={onInputChange}
                            noOptionsText={t('details.no-options')}
                            loadingText={t('details.loading-options')}
                            renderInput={(params) => (
                                <StyledTextField
                                    {...params}
                                    name="instructorEmails"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <React.Fragment>
                                                {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        )
                                    }}
                                />
                            )}
                        />
                    </>
                )}
            />
        </StyledInputContainer>
    );
};

export default AdditionalInstructors;
